/**
 * Access control utilities
 *
 * Centralised director/role checks used by:
 * - DirectorsMoneyPage
 * - DirectorsOpsPage
 * - OfficeLayout (nav pillar visibility)
 */
import type { TenantConfig } from '@/types/tenant.types';
import { UNCONFIGURED_TENANT_ID } from '@/types/tenant.types';

/**
 * Legacy Techery director names. No longer a runtime access gate — `isDirector()`
 * and OfficeLayout now resolve director access purely from `tenant.ownerIds`.
 * Retained only as the name-match seed for the `populate-owner-ids` script,
 * which looks these names up in the prod `users` collection and writes the
 * matching doc IDs to `tenant.ownerIds`.
 */
export const DIRECTOR_NAMES = ['reuben', 'casey', 'nicole'] as const;

/**
 * @deprecated Alias kept for existing imports — delegates to DIRECTOR_NAMES.
 */
export const DIRECTOR_USER_IDS = DIRECTOR_NAMES;

/** localStorage key the active tenant's ownerIds are cached under. */
const OWNER_IDS_STORAGE_KEY = 'tenant_owner_ids';

/** localStorage key the logged-in user's isAdmin custom claim is cached under. */
const IS_ADMIN_STORAGE_KEY = 'user_is_admin';

/**
 * Cache the active tenant's ownerIds to localStorage so isDirector() (a sync
 * function with no React context) can read them. Called by TenantContext on
 * every tenant load/update. Parallel to cacheTenantLocale / cacheTenantCurrency.
 *
 * Only a REAL loaded tenant may write the cache. The neutral fallback config
 * (id === UNCONFIGURED_TENANT_ID) carries no ownerIds — letting it write here
 * wiped the cache to [] on any transient tenant-load failure (offline blip,
 * terminal onSnapshot listener error), locking directors out of every
 * director-gated surface until a successful full reload. The last-known-good
 * list is preserved instead; logout still clears it explicitly via
 * clearAllAuth(), so shared-device safety is unchanged.
 */
export function cacheOwnerIds(tenant: TenantConfig | null | undefined): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (!tenant || tenant.id === UNCONFIGURED_TENANT_ID) return;
  const ids = Array.isArray(tenant.ownerIds) ? tenant.ownerIds : [];
  window.localStorage.setItem(OWNER_IDS_STORAGE_KEY, JSON.stringify(ids));
}

/** Synchronous read of the cached tenant ownerIds (empty when unset). */
export function getOwnerIdsSync(): string[] {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(OWNER_IDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((x) => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
}

/**
 * Cache the logged-in user's `isAdmin` custom claim to localStorage so the
 * sync `isAdminSync()` gate (no React context, no async token read) can use it.
 * Called from useTenantId's onAuthStateChanged whenever the token is read.
 * Parallel to cacheOwnerIds.
 */
export function cacheIsAdmin(isAdmin: boolean): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(IS_ADMIN_STORAGE_KEY, isAdmin ? 'true' : 'false');
}

/**
 * Synchronous read of the cached isAdmin claim. Fail-closed: returns false
 * when unset/unparseable so a missing cache denies (rather than grants)
 * access to admin-gated tools. The cache is refreshed on every auth state
 * change via cacheIsAdmin().
 */
export function isAdminSync(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  return window.localStorage.getItem(IS_ADMIN_STORAGE_KEY) === 'true';
}

/**
 * Check if the currently logged-in user is a director/owner.
 *
 * Resolved purely from `tenant.ownerIds` (cached at tenant load) — the
 * logged-in `user_id` is matched against them (case-insensitive). Populate
 * the field per tenant via `npm run populate:owner-ids`. The legacy
 * DIRECTOR_NAMES name-list fallback was removed once ownerIds shipped.
 *
 * ⚠️ This is a one-shot SYNC read of the cache — on a cold start it can
 * return false before TenantContext has loaded the tenant doc. For gates
 * that deny a whole page/surface, use the reactive `useDirectorAccess()`
 * hook (src/hooks/useDirectorAccess.ts) or wrap the page in
 * `<DirectorAccessGate>` so the check re-resolves when the tenant lands.
 */
export function isDirector(): boolean {
  const ownerIds = getOwnerIdsSync();
  if (ownerIds.length === 0) return false;
  const userId = (localStorage.getItem('user_id') || '').toLowerCase();
  return ownerIds.some((id) => id.toLowerCase() === userId);
}
